import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Pressable,
  Alert,
  GestureResponderEvent,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaTypography, PaprikaRadius, PaprikaZones } from '../../styles/theme';
import { FoodRecognitionResponse, DetectedFoodItem } from '../../types/foodRecognition';
import { InventoryItem } from '../../redux/inventorySlice';
import { PantryLocation, inferLocationConfident } from '../../utils/pantryLocation';
import { primaryKey } from '../../utils/pantryDuplicates';
import { flagCaptureDuplicates, CaptureDuplicateReason } from './captureDedupe';
import { EditableDetectedItem } from '../../types/foodRecognition';
import { FoodTile } from './FoodTile';
import { categoryLabelKey } from './categoryMeta';
import { captureQuantityLabel } from './captureQuantityLabel';
import { ZONE_META } from './zoneMeta';
import { PantryIcon } from './PantryIcon';
import { ZonePicker } from './ZonePicker';
import { BottomSheet } from '../weekly/v2/primitives/BottomSheet';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';

interface CaptureReviewProps {
  mode: 'fridge' | 'receipt';
  zone?: PantryLocation;
  result: Pick<FoodRecognitionResponse, 'detected_items'>;
  inventory: InventoryItem[];
  /**
   * Start every row checked instead of unchecking the foods the pantry already
   * holds. A delivery reviewed after the fact is already on the shelf, and a
   * second lot of a food is a real purchase, not a misread.
   */
  flagDuplicates?: boolean;
  /** Copy for a review that is not a fresh capture (a delivery). */
  labels?: {
    title?: string;
    subtitle?: string;
    cancel?: string;
    confirm?: (count: number) => string;
  };
  /**
   * Where the user has already said each food lives, keyed on `primaryKey`.
   * The chip has to show what the reducer will actually do, and the reducer
   * consults these first — see `inventorySlice.addItems`.
   */
  learnedZones?: Record<string, PantryLocation>;
  /**
   * `accepted` is what goes on the shelf. `reviewed` is every row as the cook
   * left it, unchecked ones included: the rows a cook removes are the scan's
   * mistakes, and they used to be dropped right here.
   */
  onConfirm: (accepted: EditableDetectedItem[], reviewed: EditableDetectedItem[]) => void;
  onCancel: () => void;
}

export const CaptureReview: React.FC<CaptureReviewProps> = ({
  mode,
  zone,
  result,
  inventory,
  learnedZones,
  flagDuplicates = true,
  labels,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();

  const isReceipt = mode === 'receipt';

  // Initialize items with duplicate detection, both against the pantry and
  // within this capture (one photo often shows a food whole *and* prepared).
  //
  // The storage zone is settled here too, once. It used to be worked out twice
  // from the same inputs — an IIFE in the row to draw the chip, and again in
  // `handleConfirm` to save — so what the user read and what got written were
  // two separate calculations that only happened to agree.
  const initialItems = useMemo(() => {
    const flags = flagDuplicates
      ? flagCaptureDuplicates(result.detected_items, inventory)
      : result.detected_items.map(() => ({ isAccepted: true, isDuplicate: false }));

    return result.detected_items.map(
      (
        item,
        index
      ): EditableDetectedItem & {
        isDuplicate?: boolean;
        duplicateReason?: CaptureDuplicateReason;
        locationOverridden?: boolean;
      } => ({
        ...item,
        id: `detected-${index}-${Date.now()}`,
        isEditing: false,
        location: isReceipt
          ? item.location ??
            inferLocationConfident(
              item.category,
              item.name,
              true,
              learnedZones?.[primaryKey(item.name)]
            ).location
          : zone,
        ...flags[index],
      })
    );
  }, [result, inventory, isReceipt, zone, learnedZones, flagDuplicates]);

  const [items, setItems] = useState(initialItems);
  const [pickingFor, setPickingFor] = useState<string | null>(null);

  const acceptedCount = items.filter(item => item.isAccepted).length;
  const duplicateCount = items.filter(item => item.isDuplicate).length;

  const handleToggleItem = (id: string) => {
    setItems(prev =>
      prev.map(item =>
        item.id === id ? { ...item, isAccepted: !item.isAccepted } : item
      )
    );
  };

  /** The user overruled the app. Remembered; the app's own guess never is. */
  const handlePickZone = (id: string, picked: PantryLocation) => {
    setItems(prev =>
      prev.map(item =>
        item.id === id ? { ...item, location: picked, locationOverridden: true } : item
      )
    );
    setPickingFor(null);
  };

  const handleConfirm = () => {
    const acceptedItems = items.filter(item => item.isAccepted);
    if (acceptedItems.length === 0) {
      Alert.alert(
        t('common.noItems', 'No Items Selected'),
        t('common.selectAtLeastOne', 'Please accept at least one food item to add to your inventory.')
      );
      return;
    }

    // `location` is already on every row — seeded once, then whatever the user
    // picked. Nothing is re-inferred at the last moment any more.
    onConfirm(acceptedItems, items);
  };

  const pickingItem = pickingFor ? items.find(item => item.id === pickingFor) ?? null : null;

  const countLabel = labels?.title ?? t(`pantry.capture.detected_${acceptedCount === 1 ? 'one' : 'other'}`, {
    count: result.detected_items.length,
  });
  const subLabel = labels?.subtitle ?? (isReceipt
    ? t('pantry.capture.reviewSubReceipt')
    : t('pantry.capture.reviewSubZone', { zone: zone && ZONE_META[zone]?.labelKey && t(ZONE_META[zone].labelKey) }));

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerChip}>
          <MaterialCommunityIcons name="lightning-bolt" size={18} color={Paprika.ai} />
        </View>
        <View style={styles.headerContent}>
          <Text style={styles.headerTitle}>{countLabel}</Text>
          <Text style={styles.headerSubtitle}>{subLabel}</Text>
        </View>
      </View>

      {/* Items List */}
      <ScrollView style={styles.itemsContainer}>
        {items.map((item, index) => (
          <TouchableOpacity
            key={item.id}
            onPress={() => handleToggleItem(item.id)}
            style={[
              styles.itemRow,
              !item.isAccepted && styles.itemRowInactive,
              index === items.length - 1 && styles.itemRowLast,
            ]}
          >
            {/* Checkbox */}
            <View
              style={[
                styles.checkbox,
                item.isAccepted && styles.checkboxChecked,
              ]}
            >
              {item.isAccepted && (
                <Text style={styles.checkmark}>✓</Text>
              )}
            </View>

            {/* Food Icon */}
            <FoodTile
              category={item.category}
              name={item.name}
              size={36}
              radius={10}
            />

            {/* Name and Sublabel */}
            <View style={styles.itemContent}>
              <Text style={styles.itemName} testID="capture-item-name">{item.name}</Text>
              <Text
                style={[
                  styles.itemSubtitle,
                  item.isDuplicate && styles.itemSubtitleDuplicate,
                ]}
              >
                {item.isDuplicate
                  ? item.duplicateReason === 'batch'
                    ? t('pantry.add.alreadyInCapture')
                    : t('pantry.add.alreadyPresent')
                  : [t(categoryLabelKey(item.category)), captureQuantityLabel(item.quantity, item.unit)]
                      .filter(Boolean)
                      .join(' · ')}
              </Text>
            </View>

            {/* Zone chip (receipt mode only).
                This announced where the app had filed the food and then took
                no correction: tapping it hit the row underneath and unchecked
                the item. It is a control now — the chevron is what says so. */}
            {isReceipt && (() => {
              const loc = item.location ?? 'pantry';
              const zoneMeta = ZONE_META[loc];
              const zoneColors = PaprikaZones[loc];
              return (
                <View style={styles.zoneChipContainer}>
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={t('pantry.capture.changeZone', { zone: t(zoneMeta.labelKey) })}
                    testID={`capture-zone-${item.id}`}
                    // The whole row toggles acceptance; this must not.
                    onPress={(e: GestureResponderEvent) => {
                      e.stopPropagation();
                      setPickingFor(item.id);
                    }}
                    style={({ pressed }: { pressed: boolean }) => [
                      styles.zoneChip,
                      { backgroundColor: zoneColors.tint, borderColor: zoneColors.accent },
                      pressed && styles.zoneChipPressed,
                    ]}
                  >
                    <PantryIcon
                      icon={zoneMeta.icon}
                      size={12}
                      color={zoneColors.accent}
                    />
                    <Text style={[styles.zoneLabel, { color: zoneColors.accent }]} numberOfLines={1}>
                      {t(zoneMeta.labelKey)}
                    </Text>
                    <MaterialCommunityIcons
                      name="chevron-down"
                      size={12}
                      color={zoneColors.accent}
                    />
                  </Pressable>
                </View>
              );
            })()}
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* Footer */}
      <View style={styles.footer}>
        <TouchableOpacity
          style={styles.cancelButton}
          onPress={onCancel}
        >
          <Text style={styles.cancelButtonText}>{labels?.cancel ?? t('pantry.capture.cancel')}</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.confirmButton}
          onPress={handleConfirm}
          testID="capture-confirm"
        >
          <Text style={styles.confirmButtonText}>
            {labels?.confirm
              ? labels.confirm(acceptedCount)
              : t(`pantry.capture.confirm_${acceptedCount === 1 ? 'one' : 'other'}`, {
                  count: acceptedCount,
                })}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Zone picker for one row. Same component as the pantry's Move sheet, so
          the shelf lives quoted here are the ones the pantry will honour. */}
      <BottomSheet visible={!!pickingItem} onClose={() => setPickingFor(null)}>
        {pickingItem && (
          <View style={styles.pickerContainer}>
            <Text style={styles.pickerEyebrow}>{t('pantry.capture.whereTitle')}</Text>
            <Text style={styles.pickerName} numberOfLines={2}>{pickingItem.name}</Text>
            <ZonePicker
              food={pickingItem}
              current={pickingItem.location}
              onPick={(picked) => handlePickZone(pickingItem.id, picked)}
            />
          </View>
        )}
      </BottomSheet>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Paprika.background,
    display: 'flex',
    flexDirection: 'column',
  },

  // Header
  header: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.hairline,
  },
  headerChip: {
    width: 34,
    height: 34,
    borderRadius: 10,
    backgroundColor: Paprika.aiSoft,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    ...PaprikaTypography.headingMedium,
    fontSize: 19,
    lineHeight: 22,
    color: Paprika.textPrimary,
  },
  headerSubtitle: {
    ...PaprikaTypography.bodySmall,
    fontSize: 11.5,
    color: Paprika.textMuted,
    marginTop: 3,
  },

  // Items List
  itemsContainer: {
    flex: 1,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 11,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.hairline,
    opacity: 1,
  },
  itemRowInactive: {
    opacity: 0.42,
  },
  itemRowLast: {
    borderBottomWidth: 0,
  },

  // Checkbox
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 7,
    borderWidth: 1.5,
    borderColor: Paprika.hairline,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  checkboxChecked: {
    backgroundColor: Paprika.ai,
    borderColor: Paprika.ai,
  },
  checkmark: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },

  // Item Content
  itemContent: {
    flex: 1,
    minWidth: 0,
  },
  itemName: {
    ...PaprikaTypography.bodyMedium,
    fontSize: 14,
    fontWeight: '500',
    color: Paprika.textPrimary,
  },
  itemSubtitle: {
    ...PaprikaTypography.bodySmall,
    fontSize: 11,
    color: Paprika.textMuted,
    marginTop: 1,
  },
  itemSubtitleDuplicate: {
    color: Paprika.accentOrange,
  },

  // Zone Chip
  zoneChipContainer: {
    flexShrink: 0,
  },
  zoneChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 9,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: Paprika.aiSoft,
    borderWidth: 1,
    borderColor: Paprika.hairline,
  },
  zoneChipPressed: {
    opacity: 0.6,
  },
  zoneLabel: {
    ...PaprikaTypography.caption,
    fontSize: 10.5,
    fontWeight: '600',
    color: Paprika.ai,
  },

  // Zone picker sheet
  pickerContainer: {
    paddingHorizontal: 16,
  },
  pickerEyebrow: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.05,
    textTransform: 'uppercase',
    color: Paprika.textMuted,
    marginBottom: 4,
  },
  pickerName: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 16,
    fontWeight: '600',
    color: Paprika.textPrimary,
    marginBottom: 13,
  },

  // Footer
  footer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: Paprika.hairline,
    backgroundColor: Paprika.cardBg,
    flexDirection: 'row',
    gap: 10,
  },
  cancelButton: {
    paddingHorizontal: 18,
    paddingVertical: 14,
    borderRadius: 13,
    backgroundColor: Paprika.surfaceLow,
  },
  cancelButtonText: {
    ...PaprikaTypography.labelLarge,
    fontSize: 13.5,
    fontWeight: '600',
    color: Paprika.textSecondary,
  },
  confirmButton: {
    flex: 1,
    paddingHorizontal: 18,
    paddingVertical: 14,
    borderRadius: 13,
    backgroundColor: Paprika.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  confirmButtonText: {
    ...PaprikaTypography.labelLarge,
    fontSize: 13.5,
    fontWeight: '600',
    color: '#fff',
  },
});
